import React, { useEffect, useRef, useState } from 'react';

import EsteirasParalelas from '../../esteiras-paralelas/EsteirasParalelas';
import AbrirEsteiraParalelaModal from '../abrir-esteira-paralela/AbrirEsteiraParalelaModal';
import { EsteiraParalelaDoCarro } from '../../../interfaces/Car';
import { useAlcanca } from '../../../hooks/usePapeis';
// O casco (`status-modal-overlay`, `LightboxStatus`) vive no CSS do StatusModal. Importado
// explicitamente: contar com o bundle carregar o arquivo da tela vizinha é o acoplamento
// invisível do BUG-V74.
import '../status/StatusModal.css';

interface ControleParalelasModalProps {
  carId: string;
  plate: string;
  /** A etapa do veículo — o que a tela contrapõe às paralelas. */
  esteiraPrincipalId?: string;
  esteiraPrincipalNome?: string;
  /**
   * `false` em etapa de fluxo livre: ali o backend recusa abrir paralela.
   *
   * ⚠️ **Ausente significa "não sei"**, e nesse caso o atalho de abrir não aparece — nem o aviso
   * que explica a ausência dele. Assumir `true` por omissão ofereceria ao Chefe um botão que o
   * backend recusaria, que é o erro previsível que o próprio modal existe para evitar.
   */
  principalEhDeTrabalho?: boolean;
  esteirasParalelas?: EsteiraParalelaDoCarro[] | null;
  esteirasLiberadasIds?: string[];
  liberacaoTotal?: boolean;
  /**
   * Trava o modal em LEITURA mesmo para quem teria as ações (usado pelo TI e pelo atendimento já
   * encerrado). Omitido, quem abre e encerra é decidido pelo papel — só o Chefe (ADMIN por
   * bypass), como o backend impõe.
   */
  somenteLeitura?: boolean;
  onCancel: () => void;
  /** Recarrega o veículo na tela hospedeira (o selo ao lado da placa muda). */
  onAlterado?: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Controle das esteiras em paralelo de um veículo (at-99, ADR 0050).
 *
 * Aberto pelo **selo ao lado da placa** (pedido do Victor, 10/09/2026), nos modais de placa em
 * que o selo aparece. Existe porque o selo respondia "há serviço em paralelo" e parava aí: quem
 * quisesse saber **qual**, **desde quando**, **quem abriu** ou quisesse **encerrar** não tinha
 * caminho nenhum fora do modal do Chefe.
 *
 * ⚠️ O corpo é o componente compartilhado `EsteirasParalelas`, o mesmo que já vive no modal do
 * Chefe — importado, nunca recopiado. Aqui ele ganha uma moldura e, para quem pode, o atalho de
 * abrir uma nova.
 *
 * ⚠️ Este modal abre **sobre** outro, e por isso todo hospedeiro precisa somá-lo à guarda de
 * "clicou fora" dele. Sem isso o hospedeiro se fecha no primeiro clique aqui dentro e leva este
 * junto — a armadilha registrada em `03-code-conventions.md`, que já mordeu três vezes.
 */
const ControleParalelasModal: React.FC<ControleParalelasModalProps> = ({
  carId,
  plate,
  esteiraPrincipalId,
  esteiraPrincipalNome,
  principalEhDeTrabalho,
  esteirasParalelas,
  esteirasLiberadasIds = [],
  liberacaoTotal = false,
  somenteLeitura = false,
  onCancel,
  onAlterado,
  sidebarRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [abrirAberto, setAbrirAberto] = useState(false);
  const [recarregar, setRecarregar] = useState(0);

  /**
   * Abrir e encerrar é do Chefe (ADMIN por bypass) — o mesmo `@Roles` das duas rotas. A decisão
   * mora aqui, e não em cada hospedeiro, porque o modal é o mesmo nas seis telas e a regra é uma
   * só: repetida seis vezes, ela divergiria na primeira tela nova. O TI não tem o papel e, por
   * consequência, abre em leitura sem precisar de gating próprio (ADR 0012).
   */
  const ehChefe = useAlcanca(['CHEFE_DE_OFICINA']);
  const podeControlar = ehChefe && !somenteLeitura;

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      // O modal de abertura é filho DESTE: enquanto ele estiver no ar, o clique nele seria lido
      // aqui como clique de fundo. Mesma guarda que o hospedeiro faz por este modal.
      if (abrirAberto) return;
      const alvo = evento.target as Node;
      if (modalRef.current?.contains(alvo)) return;
      if (sidebarRef?.current?.contains(alvo)) return;
      onCancel();
    };
    const escape = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape' && !abrirAberto) onCancel();
    };
    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', escape);
    };
  }, [onCancel, sidebarRef, abrirAberto]);

  const jaAbertasIds = (esteirasParalelas ?? []).map((p) => p.esteiraId);

  return (
    <>
      {/* z-index acima do modal de ações (1050), como o de motivo da pendência (at-89). */}
      <div className="status-modal-overlay">
        <div className="status-modal-content" ref={modalRef}>
          <div className="LightboxStatus">
            <div className="LightboxStatusForm">
              <div className="EditarCarro">Esteiras em paralelo — {plate}</div>

              <p style={{ fontSize: 13, color: 'var(--tema-texto-2, #5E5E5E)', lineHeight: 1.45, margin: '0 0 4px' }}>
                O veículo está em <strong>{esteiraPrincipalNome ?? 'sua etapa atual'}</strong>. As
                esteiras abaixo têm serviço em curso ao mesmo tempo, sem que ele saia da etapa: o
                prazo dela não reinicia e o quadro não muda de coluna.
              </p>

              <EsteirasParalelas
                carId={carId}
                podeEncerrar={podeControlar}
                recarregar={recarregar}
                onEncerrada={() => {
                  setRecarregar((valor) => valor + 1);
                  onAlterado?.();
                }}
              />

              <div className="Frame1000003998Status">
                <div className="CtaBigEditar cancel">
                  <button type="button" className="CustomFileCancel" onClick={onCancel}>
                    Fechar
                  </button>
                </div>
                {podeControlar && principalEhDeTrabalho === true && (
                  <button
                    type="button"
                    className="CtaBigEditar save"
                    onClick={() => setAbrirAberto(true)}
                  >
                    Abrir outra em paralelo
                  </button>
                )}
              </div>

              {podeControlar && principalEhDeTrabalho === false && (
                <p style={{ fontSize: 12, color: 'var(--tema-texto-2, #5E5E5E)', lineHeight: 1.4, margin: 0 }}>
                  Não é possível abrir esteira em paralelo enquanto o veículo estiver numa etapa de
                  fluxo livre. Mova-o para a etapa em que ele está sendo trabalhado.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {abrirAberto && (
        <AbrirEsteiraParalelaModal
          carId={carId}
          plate={plate}
          esteiraPrincipalId={esteiraPrincipalId}
          esteiraPrincipalNome={esteiraPrincipalNome}
          jaAbertasIds={jaAbertasIds}
          esteirasLiberadasIds={esteirasLiberadasIds}
          liberacaoTotal={liberacaoTotal}
          onCancel={() => setAbrirAberto(false)}
          onAberta={() => {
            setRecarregar((valor) => valor + 1);
            onAlterado?.();
          }}
          sidebarRef={sidebarRef}
        />
      )}
    </>
  );
};

export default ControleParalelasModal;
